import React, {useEffect, useState} from 'react';
import styled, {useTheme} from 'styled-components/native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withSequence,
  withTiming,
  Easing,
  interpolate,
} from 'react-native-reanimated';
import TextGeneric from '../textGeneric';
import Icon from '../icon';

const Container = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 16px;
  padding-vertical: 12px;
`;

const AnimatedIconWrapper = styled(Animated.View)`
  width: 70px;
  height: 70px;
  align-items: center;
  justify-content: center;
`;

const TextContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;

const LoadingText = styled(TextGeneric)`
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme}) => theme.text.s6}px;
  font-weight: 500;
  font-style: italic;
`;

const DotsContainer = styled.View`
  flex-direction: row;
  align-items: center;
  margin-left: 6px;
  height: 20px;
`;

const Dot = styled(Animated.View)<{color: string}>`
  width: 6px;
  height: 6px;
  border-radius: 3px;
  background-color: ${({color}) => color};
  margin-horizontal: 2px;
`;

// Phase 1: Before first API response (dev_message_id)
const phase1Messages = ['Analyzing case', 'Reviewing data'];

// Phase 2: After dev_message_id, before references
const phase2Messages = ['Synthesizing evidence', 'Evaluating findings'];

// Phase 3: After references arrive, before text streaming
const phase3Messages = ['Clinical reasoning', 'Finalizing output'];

interface StreamingLoadingIndicatorProps {
  phase?: 1 | 2 | 3; // Which phase of loading we're in
}

// Wavy dots component using a single master animation
const WavyDots: React.FC<{colors: string[]}> = ({colors}) => {
  // Master progress value that cycles 0 -> 1 repeatedly
  const progress = useSharedValue(0);

  useEffect(() => {
    // Single continuous animation that drives all dots
    progress.value = withRepeat(
      withTiming(1, {
        duration: 800, // One full wave cycle
        easing: Easing.linear,
      }),
      -1, // Infinite repeat
      false, // Don't reverse
    );
  }, []);

  // Create animated styles for each dot with phase offsets
  const dot0Style = useAnimatedStyle(() => {
    // Phase offset of 0
    const phase = (progress.value + 0) % 1;
    const y = interpolate(
      phase,
      [0, 0.25, 0.5, 0.75, 1],
      [0, -6, 0, 0, 0],
    );
    return {transform: [{translateY: y}]};
  });

  const dot1Style = useAnimatedStyle(() => {
    // Phase offset of 0.125 (1/8 of cycle)
    const phase = (progress.value + 0.875) % 1;
    const y = interpolate(
      phase,
      [0, 0.25, 0.5, 0.75, 1],
      [0, -6, 0, 0, 0],
    );
    return {transform: [{translateY: y}]};
  });

  const dot2Style = useAnimatedStyle(() => {
    // Phase offset of 0.25 (1/4 of cycle)
    const phase = (progress.value + 0.75) % 1;
    const y = interpolate(
      phase,
      [0, 0.25, 0.5, 0.75, 1],
      [0, -6, 0, 0, 0],
    );
    return {transform: [{translateY: y}]};
  });

  const dot3Style = useAnimatedStyle(() => {
    // Phase offset of 0.375 (3/8 of cycle)
    const phase = (progress.value + 0.625) % 1;
    const y = interpolate(
      phase,
      [0, 0.25, 0.5, 0.75, 1],
      [0, -6, 0, 0, 0],
    );
    return {transform: [{translateY: y}]};
  });

  const dotStyles = [dot0Style, dot1Style, dot2Style, dot3Style];

  return (
    <DotsContainer>
      {colors.map((color, index) => (
        <Dot key={index} style={dotStyles[index]} color={color} />
      ))}
    </DotsContainer>
  );
};

const StreamingLoadingIndicator: React.FC<StreamingLoadingIndicatorProps> = ({
  phase = 1,
}) => {
  const theme = useTheme();
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const scale = useSharedValue(1);

  // Get messages and timing based on phase
  const phaseConfig = {
    1: {messages: phase1Messages, firstMessageDuration: 2000}, // 2 seconds
    2: {messages: phase2Messages, firstMessageDuration: 3000}, // 3 seconds
    3: {messages: phase3Messages, firstMessageDuration: 3000}, // 3 seconds
  };

  const {messages, firstMessageDuration} = phaseConfig[phase];

  // Reset to first message when phase changes
  useEffect(() => {
    setCurrentMessageIndex(0);
  }, [phase]);

  // Show first message for specified duration, then switch to second and stay
  useEffect(() => {
    // If we only have one message or already on the last message, don't set timer
    if (messages.length <= 1 || currentMessageIndex >= 1) {
      return;
    }

    // Set timeout to switch to second message after the specified duration
    const timerId = setTimeout(() => {
      setCurrentMessageIndex(1);
    }, firstMessageDuration);

    return () => clearTimeout(timerId);
  }, [phase, messages.length, firstMessageDuration, currentMessageIndex]);

  // Stronger pulsing animation for the icon
  useEffect(() => {
    scale.value = withRepeat(
      withSequence(
        withTiming(1.7, {duration: 450}),
        withTiming(1, {duration: 450}),
      ),
      -1, // Infinite repeat
      false,
    );
  }, []);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{scale: scale.value}],
    };
  });

  // Dot colors - using theme primary with variations
  const dotColors = [
    theme.primary,
    theme.primary + 'CC',
    theme.primary + '99',
    theme.primary + '66',
  ];

  return (
    <Container>
      <AnimatedIconWrapper style={animatedStyle}>
        <Icon name="genio" size={theme.text.s3} color={theme.primary} />
      </AnimatedIconWrapper>
      <TextContainer>
        <LoadingText>{messages[currentMessageIndex]}</LoadingText>
        <WavyDots colors={dotColors} />
      </TextContainer>
    </Container>
  );
};

export default StreamingLoadingIndicator;
